import { useTranslation } from 'react-i18next'

import { updateUserProfile } from '../../../../../../api'
import { EMPTY_LIST } from '../../../../../../constants/common'
import { HTML_LINK_TARGET } from '../../../../../../constants/html'
import {
  USER_STATUS_CODE_BLOCKED,
  USER_STATUS_CODE_IMPORTED,
} from '../../../../../../constants/user'
import {
  useInitData,
  useNotification,
  useUserData,
} from '../../../../../../hooks'
import { getItemsIds } from '../../../../../../utils/common'
import { getPostProfilePathname } from '../../../../../../utils/link'
import { isUserStatusBlocked } from '../../../../../../utils/user'
import {
  AttachServiceRequestsModal,
  useCreateRequest,
} from '../../../../../Request'
import { updateNewsboard } from '../../../../Newsboard.api'
import {
  POST_ACTION_BLOCK_ITEM,
  POST_ACTION_BLOCK_USER,
  POST_ACTION_CONNECT_REQUEST,
  POST_ACTION_CREATE_REQUEST,
  POST_ACTION_EDIT,
  POST_ACTION_UNBLOCK_ITEM,
  POST_ACTION_UNBLOCK_USER,
} from '../../NewsboardPostHeader.constants'

import {
  NOTIFICATION_TRANSLATION_KEYS,
  POST_BLOCK_STATUS,
  POST_UNBLOCK_STATUS,
} from './useNewsboardPostHeaderItemActions.constants'
import {
  getPostHeaderActions,
  isPostBlockStatus,
} from './useNewsboardPostHeaderItemActions.utils'

export function useNewsboardPostHeaderItemActions({
  data,
  onCloseModal,
  onReload,
  onSetModal,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()
  const user = useUserData()
  const { availableSections } = useInitData()

  const { handleOpenCreation } = useCreateRequest({ setModal: onSetModal })

  const actions = getPostHeaderActions({
    permissions: data.permissions,
    t,
    user,
  })

  function handleBlockPost(spam) {
    updateNewsboard(data.uuid, { spam })
      .then(() => {
        handleAddSuccessNotification(
          NOTIFICATION_TRANSLATION_KEYS[
            isPostBlockStatus(spam)
              ? POST_ACTION_BLOCK_ITEM
              : POST_ACTION_UNBLOCK_ITEM
          ]
        )
        onReload()
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  function handleBlockUser(status) {
    updateUserProfile({ data: { status }, id: data.owner.id })
      .then(() => {
        handleAddSuccessNotification(
          NOTIFICATION_TRANSLATION_KEYS[
            isUserStatusBlocked(status)
              ? POST_ACTION_BLOCK_USER
              : POST_ACTION_UNBLOCK_USER
          ]
        )
        onReload()
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  function handleAttachRequest(value) {
    updateNewsboard(data.uuid, { requests: getItemsIds(value) })
      .then(() => {
        onCloseModal()

        handleAddSuccessNotification(
          NOTIFICATION_TRANSLATION_KEYS[POST_ACTION_CONNECT_REQUEST]
        )

        onReload()
      })
      .catch(() => {
        onCloseModal()

        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  function handleConnectCreatedRequest(request) {
    handleAttachRequest([...(data.requests || EMPTY_LIST), request])
  }

  function handleCreateRequest() {
    handleOpenCreation({
      formProps: { onSuccess: handleConnectCreatedRequest },
      wizardProps: {
        postUuid: data.uuid,
        onSuccess: onReload,
      },
    })
  }

  function handleClickEdit() {
    window.open(
      getPostProfilePathname({ availableSections, id: data.uuid }),
      HTML_LINK_TARGET.self
    )
  }

  function handleClickNewsboardPostHeaderItemAction(item) {
    switch (item.value) {
      case POST_ACTION_EDIT: {
        handleClickEdit()
        break
      }

      case POST_ACTION_BLOCK_ITEM: {
        handleBlockPost(POST_BLOCK_STATUS)
        break
      }

      case POST_ACTION_UNBLOCK_ITEM: {
        handleBlockPost(POST_UNBLOCK_STATUS)
        break
      }

      case POST_ACTION_BLOCK_USER: {
        handleBlockUser(USER_STATUS_CODE_BLOCKED)
        break
      }

      case POST_ACTION_UNBLOCK_USER: {
        handleBlockUser(USER_STATUS_CODE_IMPORTED)
        break
      }

      case POST_ACTION_CREATE_REQUEST: {
        handleCreateRequest()
        break
      }

      case POST_ACTION_CONNECT_REQUEST: {
        onSetModal(
          <AttachServiceRequestsModal
            multipleSelection
            headerProps={{
              text: 'Modal.AttachRequestsToPostTitleKey',
            }}
            initialSelection={data.requests}
            restoreAttention={false}
            onClose={onCloseModal}
            onSelectedAttachRequests={handleAttachRequest}
          />
        )
        break
      }
    }
  }

  return { actions, handleClickNewsboardPostHeaderItemAction }
}
